import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from 'semantic-ui-react'
import classNames from 'classnames'
import { GameBoard, OwnedPlanet, PlanetDeed } from '@prometheus/types'
import { useSelector } from '../../../../hooks/useSelector'
import translateOrbitingPlanetColorRaw from '@/utils/translateOrbitingPlanetColorRaw'
import { PlanetDetailsDisplay } from './PlanetDetailsDisplay'
import { TileUpgradesPopup } from './TileUpgradesPopup'
import { PlanetCyclePopup } from './PlanetCyclePopup'
import { isPlanetHoldingForUpgrade } from '@prometheus/utils'
import { MinePurchaseModal } from './MinePurchaseModal'
import PromiseDiv from '../../../../components/PromiseDiv'
import usePlayerActions from '../../hooks/usePlayerActions'
import { buildCompactLevels, buildCompactMineChips, buildUnownedMineChips, buildPriceLabel, buildTileLabel, getNextTileUpgrade, isTravelChoiceLocked, CompactMineChip } from './compactPlanet'
import { mineChipClasses, resolveMineTap } from './minePurchase'
import { findOwner } from '../../../../utils/findOwner'
import PromiseButtonV2 from '@/components/PromiseButtonV2'
import './CompactPlanetCard.scss'

interface CompactPlanetCardProps {
  planet: keyof GameBoard
  deed: PlanetDeed
  // Whether this row belongs to the owned-planets list (tile + owned mines)
  // or a choice list like the travel phase (price/rent + available mines).
  owned: boolean
  ownedPlanet?: OwnedPlanet
  // Buy-phase context (BoardPlanetSheet): the head action buys the planet
  // (upgradePlanet 'purchase') instead of traveling to it.
  purchase?: boolean
}

// Slimmed-down planet row (the phone default; desktop opts in via the same
// Compact/Full toggle): name, Pop/Req for five levels, tile
// progress + owned mines (owned list) or price/rent + available mines
// (travel list). The card's controls stay LIVE in compact form — the tile
// chip opens the tile-upgrades panel and mine chips buy/sell exactly like
// the full card — and ONLY the chevron button expands to the full card, so
// taps on the card never auto-expand it. Cards are content-sized flex items
// so several fit side by side.
export const CompactPlanetCard = ({ planet, deed, owned, ownedPlanet, purchase }: CompactPlanetCardProps) => {
  const [expanded, setExpanded] = useState(false)
  const [mineModal, setMineModal] = useState<number | null>(null)
  const levelsScrollRef = useRef<HTMLDivElement>(null)

  // The Lvl row is parked above the two-row (Pop/Req) scroll window — start
  // scrolled to the bottom so it stays hidden until the player swipes up.
  useEffect(() => {
    const el = levelsScrollRef.current
    if (el) {
      el.scrollTop = el.scrollHeight
    }
  }, [expanded])
  const disable_planet_tokens = !!useSelector(s => s.game.options?.disable_planet_tokens)
  const tile_upgrades = useSelector(s => s.game.metadata?.tile_upgrades) || []
  const uid = useSelector(s => s.auth.uid) || ''
  const turn = useSelector(s => s.game.metadata?.turn) || 0
  const players = useSelector(s => s.game.metadata?.players)
  const players_choosing_planet = useSelector(s => s.game.metadata?.players_choosing_planet) || []
  const occupied_planets = useSelector(s => Object.values(s.game.metadata?.players || {}).map(p => p.current_planet))
  const { onUpgradePlanet, onPickChoosablePlanet } = usePlayerActions()

  const orbit = planet.split(':')[0].split('|')[0]
  const color = translateOrbitingPlanetColorRaw(orbit)
  const name = planet.split(':')[1]
  const isOwnedView = owned && !!ownedPlanet

  // Same decision tree as the full card's mine chips (shared in
  // `minePurchase.ts`): buying an empty single-slot mine is one tap, every
  // other tap — sells included — opens the quantity modal to confirm.
  const onTapMine = useCallback((chip: CompactMineChip) => {
    const intent = resolveMineTap(chip.current, chip.max)
    if (intent.action === 'buy') {
      return onUpgradePlanet([planet, chip.index, intent.quantity], 'mine')
    }
    setMineModal(chip.index)
    return undefined
  }, [planet, onUpgradePlanet])

  if (expanded) {
    return (
      <div className="pg-compact-planet__expanded">
        <button type="button" className="pg-compact-planet__collapse" onClick={() => setExpanded(false)}>
          <Icon name="compress" /> Compact view
        </button>
        <PlanetDetailsDisplay choice planet={planet} deed={deed} isPurchase={false} owned={owned} ownedPlanet={ownedPlanet} />
      </div>
    )
  }

  const levels = buildCompactLevels(deed, isOwnedView ? ownedPlanet!.level : -1)
  const mines = isOwnedView ? buildCompactMineChips(ownedPlanet!, deed) : buildUnownedMineChips(deed)

  // Two separate chips on an owned planet, matching the full card: where the
  // planet is in its cycle (a status), and the tile upgrade (an action). They
  // were one chip, which made the cycle numbers look like the tile's price.
  const nextTileUpgrade = isOwnedView
    ? getNextTileUpgrade(ownedPlanet!.tile, tile_upgrades, disable_planet_tokens)
    : { label: '', available: false }
  const locked = isOwnedView && isPlanetHoldingForUpgrade(ownedPlanet!)

  const cycleChip = (
    <span className={classNames('pg-compact-planet__tile', { interactive: isOwnedView, 'is-locked': locked })}>
      {isOwnedView ? (
        <>
          <Icon name="cube" />
          {buildTileLabel(ownedPlanet!, disable_planet_tokens)}
        </>
      ) : (
        buildPriceLabel(deed)
      )}
    </span>
  )

  const tileChip = (
    <span className="pg-compact-planet__tile interactive">
      <Icon name="angle double up" />
      {nextTileUpgrade.label}
    </span>
  )

  return (
    <div className="pg-compact-planet" style={{ borderColor: color }}>
      <div className="pg-compact-planet__head">
        <button
          type="button"
          className="pg-compact-planet__name"
          style={{ color }}
          onClick={() => setExpanded(true)}
          aria-label={`${name}: open full planet card`}
        >
          {name}
        </button>
        {!owned && (
          // Live head action — same rules and orbit color as the full card,
          // shrunk to sit beside the name. Travel in choice lists; Buy in
          // the buy-phase board sheet (rent/price shows in the head chip).
          <PromiseButtonV2 ignoreGameLogic
            size="xs"
            className="pg-compact-planet__travel"
            disabled={purchase
              ? !!findOwner(planet, players || {})
              : isTravelChoiceLocked(turn, uid, players_choosing_planet, occupied_planets, planet)}
            style={{ backgroundColor: color, borderColor: color }}
            onClick={() => (purchase ? onUpgradePlanet(planet, 'purchase') : onPickChoosablePlanet(planet))}
          >
            <Icon name={purchase ? 'dollar sign' : 'rocket'} style={{ position: 'relative', top: '-2px' }} />
            {purchase ? 'Buy' : 'Travel'}
          </PromiseButtonV2>
        )}
        {isOwnedView ? (
          <>
            <PlanetCyclePopup
              trigger={cycleChip}
              disablePlanetTokens={disable_planet_tokens}
              // Only while genuinely holding: a stale counter can linger for a
              // turn after a tile Enhance un-completes the cycle.
              holdingYears={isPlanetHoldingForUpgrade(ownedPlanet!) ? ownedPlanet!.upgrade_hold_turns || 0 : 0}
              position="bottom center"
            />
            {nextTileUpgrade.available && (
              <TileUpgradesPopup planet={planet} trigger={tileChip} position="bottom center" />
            )}
          </>
        ) : (
          cycleChip
        )}
      </div>
      <div className="pg-compact-planet__levels-scroll" ref={levelsScrollRef}>
        <table className="pg-compact-planet__levels">
          <tbody>
            <tr>
              <th>Lvl</th>
              {levels.map(l => (
                <td key={l.lvl} className={classNames({ current: l.isCurrent })}>{l.lvl}</td>
              ))}
            </tr>
            <tr>
              <th>Pop</th>
              {levels.map(l => (
                <td key={l.lvl} className={classNames({ current: l.isCurrent })}>{l.pop}</td>
              ))}
            </tr>
            <tr>
              <th>Req</th>
              {levels.map(l => (
                <td key={l.lvl} className={classNames({ current: l.isCurrent })}>{l.req}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {!!mines.length && (
        <div className="pg-compact-planet__mines">
          {mines.map(chip => (
            isOwnedView ? (
              <PromiseDiv
                key={chip.key}
                className={classNames('pg-compact-planet__mine interactive', mineChipClasses(chip.resource, chip.current), { active: chip.active })}
                onClick={() => onTapMine(chip)}
              >
                {chip.label}
              </PromiseDiv>
            ) : (
              <span key={chip.key} className={classNames('pg-compact-planet__mine', mineChipClasses(chip.resource, chip.current), { active: chip.active })}>
                {chip.label}
              </span>
            )
          ))}
        </div>
      )}
      {/* Every owned slot needs a modal now, not just the multi-slot ones —
          selling a single-slot mine routes through the quantity screen too. */}
      {isOwnedView && mines.map(chip => (
        <MinePurchaseModal
          key={`modal-${chip.key}`}
          open={mineModal === chip.index}
          setOpen={(open: boolean) => setMineModal(open ? chip.index : null)}
          planet={planet}
          index={chip.index}
          resource={chip.resource}
        />
      ))}
    </div>
  )
}

export default CompactPlanetCard
